import { T } from "@repo/transifex";
import { Button } from "~/components";
import { getScaleUnitLabel } from "~/utils/weight-units";

import type { ScaleReading } from "./scale-precision-step";
import { ScaleStatusView } from "./scale-status-view";

type ScaleConnectionStatusProps = {
  phase: "connecting" | "connected" | "error";
  reading: ScaleReading | null;
  onCancel: () => void;
  onDone: () => void;
  onRetry: () => void;
};

/** The skip path: pairing saved, now checking the scale answers. */
export const ScaleConnectionStatus = ({
  phase,
  reading,
  onCancel,
  onDone,
  onRetry
}: ScaleConnectionStatusProps) => {
  if (phase === "connecting") {
    return (
      <ScaleStatusView
        actions={
          <Button
            className="min-w-[200px]"
            onClick={onCancel}
            variant="secondary"
          >
            <T _str="Cancel" />
          </Button>
        }
        description={
          <T _str="Using the current precision. You can fine-tune it any time from Configure precision." />
        }
        title={<T _str="Connecting to scale" />}
        tone="loading"
      />
    );
  }

  if (phase === "connected") {
    return (
      <ScaleStatusView
        actions={
          <Button className="min-w-[200px]" onClick={onDone} variant="primary">
            <T _str="Back to settings" />
          </Button>
        }
        description={
          reading ? (
            <T
              _str="Weights will show with {count} decimals in {unit}. Run Configure precision any time to check it against the scale's own display."
              count={reading.decimals}
              unit={getScaleUnitLabel(reading.unit)}
            />
          ) : (
            <T _str="Run Configure precision any time to check the reading against the scale's own display." />
          )
        }
        title={<T _str="Scale connected" />}
        tone="success"
      />
    );
  }

  return (
    <ScaleStatusView
      actions={
        <>
          <Button className="flex-1" onClick={onDone} variant="secondary">
            <T _str="Back to settings" />
          </Button>
          <Button className="flex-[1.4]" onClick={onRetry} variant="primary">
            <T _str="Retry" />
          </Button>
        </>
      }
      description={
        <T _str="Check that the scale is powered on and plugged in, then try again." />
      }
      title={<T _str="Couldn't connect to the scale" />}
      tone="error"
    />
  );
};
